import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type Ref,
} from 'react';
type InputProps = InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> };
export function Input(props: InputProps) {
  const ref = useRef<HTMLInputElement>(null),
    [files, setFiles] = useState<File[]>([]),
    [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    const next = files.filter((f) => f.type.startsWith('image/')).map((f) => URL.createObjectURL(f));
    setUrls(next);
    return () => next.forEach(URL.revokeObjectURL);
  }, [files]);
  if (props.hidden) return <input {...props} />;
  if (props.type === 'file')
    return (
      <div
        className="replay-file"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (props.disabled || !ref.current) return;
          const dt = new DataTransfer();
          Array.from(e.dataTransfer.files)
            .slice(0, props.multiple ? undefined : 1)
            .forEach((f) => dt.items.add(f));
          ref.current.files = dt.files;
          ref.current.dispatchEvent(new Event('change', { bubbles: true }));
        }}
      >
        <input
          {...props}
          ref={ref}
          hidden
          className="replay-file-input"
          onChange={(e) => {
            setFiles(Array.from(e.target.files ?? []));
            props.onChange?.(e);
          }}
        />
        <button type="button" className="btn btn-ghost" disabled={props.disabled} onClick={() => ref.current?.click()}>
          Seleccionar {props.multiple ? 'archivos' : 'archivo'}
        </button>
        <small>Arrastra aquí · {props.accept || 'Todos los archivos'}</small>
        {files.map((f, i) => (
          <small key={i}>{f.name}</small>
        ))}
        {urls.map((url) => (
          <img key={url} src={url} alt="Vista previa" />
        ))}
      </div>
    );
  if (props.type === 'number' || props.type === 'range') {
    const step = (direction: number) => {
      const node = ref.current;
      if (!node) return;
      const increment = props.step === 'any' ? 1 : Number(props.step ?? 1),
        next = Math.max(Number(props.min ?? -Infinity), Math.min(Number(props.max ?? Infinity), Number(node.value || 0) + increment * direction));
      Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!.set!.call(node, String(Number(next.toFixed(8))));
      node.dispatchEvent(new Event('input', { bubbles: true }));
    };
    return (
      <div className="replay-stepper" data-nav-row>
        <button type="button" className="btn btn-sm" aria-label="Reducir valor" disabled={props.disabled} onClick={() => step(-1)}>
          −
        </button>
        <input {...props} ref={ref} className={`input replay-control ${props.className ?? ''}`} />
        {props.type === 'range' && <output>{props.value}</output>}
        <button type="button" className="btn btn-sm" aria-label="Aumentar valor" disabled={props.disabled} onClick={() => step(1)}>
          +
        </button>
      </div>
    );
  }
  return <input {...props} className={`replay-control ${props.className ?? (['checkbox', 'radio'].includes(props.type ?? '') ? '' : 'input')}`} />;
}
export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`textarea ${props.className ?? ''}`} />;
}
export function TextField({ label, hint, error, ...props }: InputProps & { label: string; hint?: string; error?: string }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <Input {...props} id={id} aria-invalid={!!error} aria-describedby={hint || error ? `${id}-hint` : undefined} />
      {(hint || error) && (
        <small id={`${id}-hint`} role={error ? 'alert' : undefined}>
          {error || hint}
        </small>
      )}
    </div>
  );
}
export function TextArea({ label, hint, error, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string; error?: string }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <Textarea {...props} id={id} aria-invalid={!!error} aria-describedby={`${id}-hint`} />
      <small id={`${id}-hint`}>{error || hint}</small>
    </div>
  );
}
export function Checkbox({
  label,
  help,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  help?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="check">
      <Input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {help && <small>{help}</small>}
      </span>
    </label>
  );
}
export function Switch({ label, help, checked, onChange, disabled }: Parameters<typeof Checkbox>[0]) {
  const id = useId();
  return (
    <label className="check replay-switch">
      <Input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-describedby={help ? id : undefined}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {label}
        {help && <small id={id}>{help}</small>}
      </span>
    </label>
  );
}
export const NumberField = (props: Parameters<typeof TextField>[0]) => <TextField {...props} type="number" />;
export const Slider = (props: Parameters<typeof TextField>[0]) => <TextField {...props} type="range" />;
export function FileDrop({ label, onFiles, ...props }: Omit<InputProps, 'onChange' | 'type'> & { label: string; onFiles: (files: File[]) => void }) {
  return (
    <label className="field">
      <span>{label}</span>
      <Input {...props} aria-label={label} type="file" onChange={(e) => onFiles(Array.from(e.target.files ?? []))} />
    </label>
  );
}
function plain(node: ReactNode): string {
  return Children.toArray(node)
    .map((n) => (isValidElement(n) ? plain((n.props as { children?: ReactNode }).children) : String(n ?? '')))
    .join(' ');
}
function optionList(children: ReactNode): { value: string; label: string; disabled?: boolean }[] {
  return Children.toArray(children).flatMap((c) => {
    if (!isValidElement(c)) return [];
    const p = c.props as { value?: string | number; children?: ReactNode; disabled?: boolean };
    return c.type === 'option' ? [{ value: String(p.value ?? plain(p.children)), label: plain(p.children), disabled: p.disabled }] : optionList(p.children);
  });
}
export function Select({
  label,
  value,
  onChange,
  children,
  required,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
  required?: boolean;
  disabled?: boolean;
}) {
  const id = useId(),
    root = useRef<HTMLDivElement>(null),
    trigger = useRef<HTMLButtonElement>(null),
    [open, setOpen] = useState(false),
    [query, setQuery] = useState(''),
    [active, setActive] = useState(0),
    options = optionList(children),
    filtered = options.filter((o) => o.label.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  useEffect(() => {
    if (open) document.getElementById(`${id}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open, id]);
  const choose = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery('');
    trigger.current?.focus();
  };
  return (
    <div
      className="field replay-select"
      ref={root}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
        if (open && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          setActive((i) =>
            e.key === 'Home' ? 0 : e.key === 'End' ? filtered.length - 1 : Math.max(0, Math.min(filtered.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1))),
          );
        }
        if (open && e.key === 'Enter') {
          e.preventDefault();
          const o = filtered[active];
          if (o && !o.disabled) choose(o.value);
        }
      }}
    >
      <span id={`${id}-label`}>
        {label}
        {required ? ' *' : ''}
      </span>
      <button
        ref={trigger}
        type="button"
        className="input replay-select-trigger"
        disabled={disabled}
        aria-labelledby={`${id}-label ${id}-value`}
        aria-haspopup="listbox"
        aria-required={required}
        aria-expanded={open}
        aria-controls={`${id}-list`}
        onClick={() => {
          setOpen(!open);
          setActive(
            Math.max(
              0,
              options.findIndex((o) => o.value === value),
            ),
          );
        }}
        onKeyDown={(e) => {
          if (!open && ['ArrowDown', 'ArrowUp'].includes(e.key)) {
            e.preventDefault();
            setOpen(true);
          }
          if (!open && e.key.length === 1 && !e.ctrlKey && !e.altKey && e.key !== ' ') {
            setQuery(e.key);
            setActive(0);
            setOpen(true);
          }
        }}
      >
        <span id={`${id}-value`}>{options.find((o) => o.value === value)?.label || 'Seleccionar'}</span>
        <span aria-hidden>⌄</span>
      </button>
      {open && (
        <div className="replay-select-popover" data-nav-scope>
          <input
            autoFocus
            className="input"
            aria-label={`Buscar en ${label}`}
            role="combobox"
            aria-expanded="true"
            aria-controls={`${id}-list`}
            aria-activedescendant={filtered[active] ? `${id}-${active}` : undefined}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
          />
          <div id={`${id}-list`} role="listbox" aria-labelledby={`${id}-label`}>
            {filtered.map((o, i) => (
              <button
                type="button"
                role="option"
                id={`${id}-${i}`}
                key={o.value}
                aria-selected={o.value === value}
                disabled={o.disabled}
                className={`replay-option ${active === i ? 'current' : ''}`}
                onClick={() => choose(o.value)}
              >
                {o.label}
              </button>
            ))}
            {!filtered.length && <p className="notice">No hay resultados. Prueba otra búsqueda.</p>}
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setOpen(false);
              trigger.current?.focus();
            }}
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { id: string; label: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  return (
    <fieldset className="admin-picker">
      <legend>
        {label} ({selected.length})
      </legend>
      <div className="row" data-nav-row>
        {selected.map((id) => (
          <button
            type="button"
            className="chip"
            key={id}
            aria-label={`Quitar ${options.find((o) => o.id === id)?.label}`}
            onClick={() => onChange(selected.filter((v) => v !== id))}
          >
            {options.find((o) => o.id === id)?.label} ×
          </button>
        ))}
      </div>
      <TextField label={`Buscar ${label.toLocaleLowerCase()}`} type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
      <div className="admin-options">
        {options
          .filter((o) => o.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
          .map((o) => (
            <Checkbox
              key={o.id}
              label={o.label}
              checked={selected.includes(o.id)}
              onChange={(v) => onChange(v ? [...selected, o.id] : selected.filter((id) => id !== o.id))}
            />
          ))}
        {!options.length && <p className="notice">No hay elementos disponibles. Añádelos primero en su sección.</p>}
      </div>
    </fieldset>
  );
}
export function Tabs({ items, value, onChange }: { items: { id: string; label: string }[]; value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={ref}
      className="tabs"
      role="tablist"
      data-nav-row
      onKeyDown={(e) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault();
        const i = items.findIndex((o) => o.id === value),
          n = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length;
        onChange(items[n]!.id);
        ref.current?.querySelectorAll('button')[n]?.focus();
      }}
    >
      {items.map((o) => (
        <button type="button" role="tab" tabIndex={value === o.id ? 0 : -1} aria-selected={value === o.id} key={o.id} onClick={() => onChange(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
export function RadioGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { id: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  const name = useId();
  return (
    <fieldset>
      <legend>{label}</legend>
      <div className="row" data-nav-row>
        {options.map((o) => (
          <label className="check" key={o.id}>
            <Input type="radio" name={name} checked={value === o.id} onChange={() => onChange(o.id)} />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
export const Segmented = RadioGroup;
export function Badge({ children, status = '' }: { children: ReactNode; status?: string }) {
  return <span className={`tag ${status}`}>{children}</span>;
}
export function EmptyState({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}
export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <fieldset className="field">
      <legend>{label}</legend>
      {children}
      {hint && <small>{hint}</small>}
    </fieldset>
  );
}
export function RowActions({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      box.current?.querySelector<HTMLButtonElement>('.replay-actions-toggle')?.focus();
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc, true);
    box.current?.querySelector<HTMLElement>('.replay-actions-menu button:not(:disabled), .replay-actions-menu a')?.focus();
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc, true);
    };
  }, [open]);
  return (
    <div className="replay-actions" ref={box}>
      <button type="button" className="btn btn-ghost btn-sm replay-actions-toggle" aria-haspopup="true" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true">⋯</span> Acciones
      </button>
      {open && (
        <div className="replay-actions-menu" id={id} data-nav-scope onClick={(e) => (e.target as HTMLElement).closest('button, a') && setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}
export function DataTable({ headers, children, pageSize = 10 }: { headers: string[]; children: ReactNode; pageSize?: number }) {
  const ref = useRef<HTMLTableElement>(null),
    [search, setSearch] = useState(''),
    [page, setPage] = useState(0),
    [sort, setSort] = useState<{ column: number; desc: boolean } | null>(null),
    [texts, setTexts] = useState<string[][]>([]);
  const rows = Children.toArray(children).map((row) => {
    if (!isValidElement<{ children?: ReactNode }>(row) || row.type !== 'tr') return row;
    return cloneElement(row, {
      children: Children.map(row.props.children, (cell, i) => {
        if (!isValidElement<{ children?: ReactNode }>(cell) || !headers[i]?.toLocaleLowerCase().includes('acciones') || !cell.props.children) return cell;
        return cloneElement(cell, { children: <RowActions>{cell.props.children}</RowActions> });
      }),
    });
  });
  useEffect(() => {
    const next: string[][] = [];
    ref.current?.querySelectorAll<HTMLTableSectionElement>('tbody[data-index]').forEach((body) => {
      const cells = Array.from(body.querySelectorAll('td'));
      cells.forEach((c, i) => {
        c.dataset.label = headers[i] ?? '';
      });
      next[Number(body.dataset.index)] = cells.map((c) => c.textContent ?? '');
    });
    setTexts((old) => (JSON.stringify(old) === JSON.stringify(next) ? old : next));
  }, [children, headers]);
  let indexes = rows.map((_, i) => i).filter((i) => !search || (texts[i] ?? []).join(' ').toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  if (sort)
    indexes.sort((a, b) => (texts[a]?.[sort.column] ?? '').localeCompare(texts[b]?.[sort.column] ?? '', 'es', { numeric: true }) * (sort.desc ? -1 : 1));
  const pages = Math.max(1, Math.ceil(indexes.length / pageSize)),
    current = Math.min(page, pages - 1),
    visible = new Set(indexes.slice(current * pageSize, current * pageSize + pageSize)),
    ordered = [...indexes, ...rows.map((_, i) => i).filter((i) => !indexes.includes(i))];
  return (
    <div className="stack replay-data-table">
      <TextField
        label="Buscar en la tabla"
        type="search"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
      />
      <div className="replay-mobile-sort">
        <Select label="Ordenar por" value={sort ? String(sort.column) : ''} onChange={(v) => setSort(v === '' ? null : { column: Number(v), desc: false })}>
          <option value="">Orden original</option>
          {headers.map((h, i) => (
            <option key={i} value={i}>
              {h}
            </option>
          ))}
        </Select>
        <button type="button" className="btn btn-sm" disabled={!sort} onClick={() => sort && setSort({ ...sort, desc: !sort.desc })}>
          {sort?.desc ? 'Descendente' : 'Ascendente'}
        </button>
      </div>
      <div className="table-wrap admin-table-wrap">
        <table ref={ref} className="table">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} scope="col" aria-sort={sort?.column === i ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
                  <button type="button" className="replay-sort" onClick={() => setSort({ column: i, desc: sort?.column === i ? !sort.desc : false })}>
                    {h} {sort?.column === i ? (sort.desc ? '↓' : '↑') : '↕'}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          {ordered.map((i) => (
            <tbody key={isValidElement(rows[i]) ? (rows[i].key ?? i) : i} data-index={i} hidden={!visible.has(i)}>
              {rows[i]}
            </tbody>
          ))}
        </table>
      </div>
      {!indexes.length && <EmptyState title="No hay resultados">Prueba otra búsqueda o añade un elemento para empezar.</EmptyState>}
      <div className="row between" data-nav-row>
        <span className="small muted">
          {indexes.length} resultados · Página {current + 1} de {pages}
        </span>
        <div className="row">
          <button type="button" className="btn btn-sm" disabled={!current} onClick={() => setPage(current - 1)}>
            Anterior
          </button>
          <button type="button" className="btn btn-sm" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}
